import axios, {AxiosError, AxiosInstance} from 'axios';
import * as AxiosLogger from 'axios-logger';

import {makeObservable, observable, runInAction} from "mobx";
import Token from "./token";
import UnauthorizedError, {CaptchaError, NetworkError} from "./types";
import {
  BookingGroupResponse,
  BookingVisibility,
  ListResponse,
  ListVisibility,
  ParserResponse,
  TokenResponse,
  UserResponse,
  WishlistItemDeleteResponse,
  WishlistItemResponse
} from "./responses";

type CropArea = { width: number, height: number, x: number, y: number };
type Picture = string | Blob | MediaSource | null;

export type ProfilePayload = {
  name?: string;
  username?: string;
  description?: string;
  userpic?: string | null;
  allow_booking?: boolean;
  booking_visibility?: BookingVisibility;
};

export default class API {
  private readonly BASE_URL = "https://api.wishlists.cc/v1/"
  private readonly checkAuthInterval = 60000;
  private readonly tokenStorage = "app.wishlists.access_token"

  /** Only ever one scheduled session check, however many pages ask for one. */
  private authTimer: ReturnType<typeof setTimeout> | null = null;
  /** The backend rotates the refresh token, so concurrent refreshes must share one call. */
  private refreshInFlight: Promise<Token | null> | null = null;

  public isLoading: boolean = true;

  public loggedIn: boolean = false;
  public loggedInUser: UserResponse | null = null;
  public token: Token | null = null;

  private client: AxiosInstance;

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      loggedIn: observable,
      loggedInUser: observable,
    });

    this.client = axios.create({baseURL: this.BASE_URL});
    this.client.interceptors.response.use(
      AxiosLogger.responseLogger,
      AxiosLogger.errorLogger,
    );
  }

  private static authHeader(token: Token) {
    return {
      headers: {
        Authorization: `Bearer ${token.accessToken}`,
      },
    };
  }

  async logout() {
    runInAction(() => {
      this.loggedIn = false;
      this.loggedInUser = null;
      localStorage.removeItem(this.tokenStorage);
    })
  }

  async uploadFile(file: Blob | MediaSource, cropArea: CropArea | null, userpic: boolean): Promise<string | null> {
    try {
      return await this.makeAuthenticatedRequest((token) => this.doUploadFile(token, file, cropArea, userpic));
    } catch (error) {
      return null;
    }
  }

  async doUploadFile(token: Token, file: any, cropArea: CropArea | null, userpic: boolean): Promise<string | null> {
    const formData = new FormData();
    formData.append("file", file)

    if (cropArea) {
      formData.append("crop[width]", "" + cropArea.width);
      formData.append("crop[height]", "" + cropArea.height);
      formData.append("crop[x_offset]", "" + cropArea.x);
      formData.append("crop[y_offset]", "" + cropArea.y);
    }

    const {data} = await this.client.post('https://files.mznx.dev/upload', formData, {
      headers: {
        Authorization: `Bearer ${token.accessToken}`,
        'X-Service-Id': userpic ? 'test-service-nPeZzxSU3eTfctpJ' : 'bc7a5af5-974b-4923-8ad5-572d0c3bd1c9'
      },
    });

    return data.url || null;
  }

  async auth(email: string, captchaAnswer: string) {
    try {
      const {status, data} = await this.client.post('signin', {
        email,
        captcha_answer: captchaAnswer,
      });
      if (status !== 200) {
        return null;
      }

      return data;
    } catch (error: any | AxiosError) {
      if (axios.isAxiosError(error) && error.response) {
        const response = error.response;
        if (response.data.detail.length) {
          for (let err in response.data.detail) {
            // @ts-ignore
            if (JSON.stringify(err["loc"]) === JSON.stringify(["body", "captcha_answer"])) {
              throw new CaptchaError('Invalid captcha');
            }
          }
        }
      }
      return null;
    }
  }

  async getToken(email: string, code: string): Promise<boolean> {
    const token = await this.doGetToken(email, code);
    if (token === null) {
      return false;
    }

    this.persistToken(token);
    return true;
  }

  async getMe(token: Token): Promise<UserResponse | null> {
    try {
      const {data} = await this.client.get('me', API.authHeader(token));
      return data;
    } catch (error) {
      return null;
    }
  }

  async getUser(username: string): Promise<UserResponse | null> {
    try {
      const {data} = await this.client.get("user/" + username);
      return data;
    } catch (error) {
      return null;
    }
  }

  async getItem(uuid: string): Promise<WishlistItemResponse | null> {
    try {
      const {data} = await this.client.get("/wishlist/items/" + uuid);
      return data;
    } catch (error) {
      return null;
    }
  }

  async patchProfile(payload: ProfilePayload): Promise<UserResponse | null> {
    try {
      return await this.makeAuthenticatedRequest((token) => this.doPatchProfile(token, payload));
    } catch (error) {
      return null;
    }
  }

  async checkAuthorization(): Promise<boolean> {
    try {
      const credentials = localStorage.getItem(this.tokenStorage);
      if (!credentials) {
        this.resetAuthState({clearUser: true, loading: false});
        return false;
      }

      let token: Token | null = Token.deserialize(credentials);
      let user = await this.getMe(token);
      if (!user) {
        if (!token.isCanBeRefreshed()) {
          this.resetAuthState({removeStorage: true, clearUser: true, loading: false});
          return false;
        }

        token = await this.refreshToken(token);
        if (!token) {
          // Either the refresh token was rejected (the session is already cleared) or the
          // call failed temporarily — in that case keep the credentials and retry later.
          runInAction(() => {
            this.isLoading = false;
          });
          this.scheduleAuthCheck();
          return false;
        }

        user = await this.getMe(token);
      }

      runInAction(() => {
        this.isLoading = false;
        this.token = token;
        this.loggedIn = true;
        user !== null && (this.loggedInUser = user);
      });
      this.scheduleAuthCheck();
      return true;
    } catch (error) {
      console.error('Keychain could not be accessed!', error);
      return false;
    }
  }

  /** Replaces any pending check, so parallel callers never stack up polling loops. */
  private scheduleAuthCheck() {
    if (this.authTimer !== null) {
      clearTimeout(this.authTimer);
    }
    this.authTimer = setTimeout(() => {
      this.authTimer = null;
      this.checkAuthorization();
    }, this.checkAuthInterval);
  }

  async getItems(username?: string): Promise<WishlistItemResponse[]> {
    if (username) {
      return await this.doGetItemsPublic(username);
    }

    if (!this.loggedIn || !this.token) {
      return [];
    }

    return await this.makeAuthenticatedRequest((token) => this.doGetItems(token));
  }

  async parseResource(url: string): Promise<ParserResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    return await this.makeAuthenticatedRequest((token) => this.doParseResource(token, url));
  }

  async createItem(name: string, description: string | null, picture: Picture, url: string | null): Promise<WishlistItemResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    const uploadedPicture = await this.resolvePicture(picture);
    return await this.makeAuthenticatedRequest((token) => this.doCreateItem(token, name, description, uploadedPicture, url));
  }

  async patchItem(uuid: string, name: string, description: string | null, picture: Picture, url: string | null): Promise<WishlistItemResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    const uploadedPicture = await this.resolvePicture(picture);
    return await this.makeAuthenticatedRequest((token) => this.doPatchItem(token, uuid, name, description, uploadedPicture, url));
  }

  async deleteItem(uuid: string): Promise<WishlistItemDeleteResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    return await this.makeAuthenticatedRequest((token) => this.doDeleteItem(token, uuid));
  }

  async updateSorting(items: string[]): Promise<WishlistItemResponse[]> {
    if (!this.loggedIn || !this.token) {
      return [];
    }

    return await this.makeAuthenticatedRequest((token) => this.doUpdateSorting(token, items));
  }

  // --- Lists (named wishlists per occasion) ---

  async getLists(): Promise<ListResponse[]> {
    if (!this.loggedIn || !this.token) {
      return [];
    }

    return await this.makeAuthenticatedRequest((token) => this.doGetLists(token));
  }

  async getListsByUsername(username: string): Promise<ListResponse[]> {
    try {
      const {data} = await this.client.get(`/user/${username}/lists`);
      return data;
    } catch (error) {
      return [];
    }
  }

  async getList(uuid: string): Promise<ListResponse | null> {
    try {
      const {data} = await this.client.get(`/lists/${uuid}`, this.optionalAuthHeader());
      return data;
    } catch (error) {
      return null;
    }
  }

  async getListItems(uuid: string): Promise<WishlistItemResponse[]> {
    try {
      const {data} = await this.client.get(`/lists/${uuid}/items`, this.optionalAuthHeader());
      return data;
    } catch (error) {
      return [];
    }
  }

  async createList(name: string, description: string | null, visibility: ListVisibility,
                   bookingVisibility: BookingVisibility): Promise<ListResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    return await this.makeAuthenticatedRequest((token) =>
      this.doPersistList(token, 'post', '/lists', name, description, visibility, bookingVisibility));
  }

  async patchList(uuid: string, name: string, description: string | null, visibility: ListVisibility,
                  bookingVisibility: BookingVisibility): Promise<ListResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    return await this.makeAuthenticatedRequest((token) =>
      this.doPersistList(token, 'patch', `/lists/${uuid}`, name, description, visibility, bookingVisibility));
  }

  async deleteList(uuid: string): Promise<WishlistItemDeleteResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    return await this.makeAuthenticatedRequest((token) => this.doDeleteList(token, uuid));
  }

  // --- Bookings ---

  /** Books an item for the current user. Returns null when it could not be booked. */
  async bookItem(itemUuid: string): Promise<WishlistItemResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    return await this.makeAuthenticatedRequest((token) => this.guarded(null, async () => {
      const {data} = await this.client.post(`/wishlist/items/${itemUuid}/booking`, {}, API.authHeader(token));
      return data;
    }));
  }

  async unbookItem(itemUuid: string): Promise<WishlistItemResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    return await this.makeAuthenticatedRequest((token) => this.guarded(null, async () => {
      const {data} = await this.client.delete(`/wishlist/items/${itemUuid}/booking`, API.authHeader(token));
      return data;
    }));
  }

  async getMyBookings(): Promise<BookingGroupResponse[]> {
    if (!this.loggedIn || !this.token) {
      return [];
    }

    return await this.makeAuthenticatedRequest((token) => this.guarded<BookingGroupResponse[]>([], async () => {
      const {data} = await this.client.get("/bookings", API.authHeader(token));
      return data;
    }));
  }

  async createListItem(listUuid: string, name: string, description: string | null, picture: Picture, url: string | null): Promise<WishlistItemResponse | null> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    const uploadedPicture = await this.resolvePicture(picture);
    return await this.makeAuthenticatedRequest((token) => this.doCreateListItem(token, listUuid, name, description, uploadedPicture, url));
  }

  async updateListSorting(listUuid: string, items: string[]): Promise<WishlistItemResponse[]> {
    if (!this.loggedIn || !this.token) {
      return [];
    }

    return await this.makeAuthenticatedRequest((token) => this.doUpdateListSorting(token, listUuid, items));
  }

  private doParseResource(token: Token, url: string): Promise<ParserResponse | null> {
    return this.guarded(null, async () => {
      const {data} = await this.client.post("/parse", {url}, API.authHeader(token));
      return data;
    });
  }

  private doCreateItem(token: Token, name: string, description: string | null, picture: string | null, url: string | null): Promise<WishlistItemResponse | null> {
    return this.guarded(null, async () => {
      const {data} = await this.client.post("/wishlist/items", {name, description, image: picture, url}, API.authHeader(token));
      return data;
    });
  }

  private doPatchItem(token: Token, uuid: string, name: string, description: string | null, picture: string | null, url: string | null): Promise<WishlistItemResponse | null> {
    return this.guarded(null, async () => {
      const {data} = await this.client.patch(`/wishlist/items/${uuid}`, {name, description, image: picture, url}, API.authHeader(token));
      return data;
    });
  }

  private doDeleteItem(token: Token, uuid: string): Promise<WishlistItemDeleteResponse | null> {
    return this.guarded(null, async () => {
      const {data} = await this.client.delete(`/wishlist/items/${uuid}`, API.authHeader(token));
      return data;
    });
  }

  private doUpdateSorting(token: Token, items: string[]): Promise<WishlistItemResponse[]> {
    return this.guarded<WishlistItemResponse[]>([], async () => {
      const {data} = await this.client.put("/wishlist/items", {items}, API.authHeader(token));
      return data;
    });
  }

  private doGetItems(token: Token): Promise<WishlistItemResponse[]> {
    return this.guarded<WishlistItemResponse[]>([], async () => {
      const {data} = await this.client.get("/wishlist/items", API.authHeader(token));
      return data;
    });
  }

  private doGetItemsPublic(username?: string): Promise<WishlistItemResponse[]> {
    return this.guarded<WishlistItemResponse[]>([], async () => {
      const {data} = await this.client.get(`/wishlist/${username}/items`);
      return data;
    });
  }

  private doPatchProfile(token: Token, payload: ProfilePayload): Promise<UserResponse | null> {
    return this.guarded(null, async () => {
      // only send what was actually provided, so a partial update never clears other fields
      const body = Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
      );

      const {data} = await this.client.patch('me', body, API.authHeader(token));
      return data;
    });
  }

  private doGetLists(token: Token): Promise<ListResponse[]> {
    return this.guarded<ListResponse[]>([], async () => {
      const {data} = await this.client.get("/lists", API.authHeader(token));
      return data;
    });
  }

  private doPersistList(token: Token, method: 'post' | 'patch', path: string, name: string, description: string | null,
                        visibility: ListVisibility, bookingVisibility: BookingVisibility): Promise<ListResponse | null> {
    return this.guarded(null, async () => {
      const {data} = await this.client[method](path, {
        name,
        description,
        visibility,
        booking_visibility: bookingVisibility,
      }, API.authHeader(token));
      return data;
    });
  }

  private doDeleteList(token: Token, uuid: string): Promise<WishlistItemDeleteResponse | null> {
    return this.guarded(null, async () => {
      const {data} = await this.client.delete(`/lists/${uuid}`, API.authHeader(token));
      return data;
    });
  }

  private doCreateListItem(token: Token, listUuid: string, name: string, description: string | null, picture: string | null, url: string | null): Promise<WishlistItemResponse | null> {
    return this.guarded(null, async () => {
      const {data} = await this.client.post(`/lists/${listUuid}/items`, {name, description, image: picture, url}, API.authHeader(token));
      return data;
    });
  }

  private doUpdateListSorting(token: Token, listUuid: string, items: string[]): Promise<WishlistItemResponse[]> {
    return this.guarded<WishlistItemResponse[]>([], async () => {
      const {data} = await this.client.put(`/lists/${listUuid}/items`, {items}, API.authHeader(token));
      return data;
    });
  }

  private optionalAuthHeader() {
    return this.token ? API.authHeader(this.token) : {};
  }

  private async doGetToken(email: string, code: string): Promise<TokenResponse | null> {
    try {
      const {status, data} = await this.client.post('token', {email, code});
      if (status !== 200) {
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  }

  private async makeAuthenticatedRequest(request: (token: Token) => Promise<any>): Promise<any> {
    if (!this.loggedIn || !this.token) {
      return null;
    }

    try {
      return await request(this.token);
    } catch (error: UnauthorizedError | any) {
      if (!(error instanceof UnauthorizedError)) {
        return null;
      }

      if (!this.token.isCanBeRefreshed()) {
        this.resetAuthState({removeStorage: true});
        return null;
      }

      const token = await this.refreshToken(this.token);
      if (!token) {
        this.resetAuthState({removeStorage: true});
        return null;
      }

      return await request(token);
    }
  }

  private async refreshToken(token: Token | null): Promise<Token | null> {
    if (token === null) {
      return null;
    }

    // Every refresh rotates the token server-side, so a second concurrent call would
    // present an already-invalidated token and needlessly end the session. Share one.
    if (this.refreshInFlight) {
      return await this.refreshInFlight;
    }

    this.refreshInFlight = this.doRefresh(token);
    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  private async doRefresh(token: Token): Promise<Token | null> {
    let refreshedToken: TokenResponse | null;
    try {
      refreshedToken = await this.doRefreshToken(token);
    } catch (err) {
      if (err instanceof NetworkError && window.location.pathname !== '/') {
        window.location.href = '/';
      }
      // Temporary failure: keep the stored credentials so the next check can recover.
      return null;
    }

    if (refreshedToken === null) {
      // The server rejected the refresh token — this session really is over.
      this.resetAuthState({removeStorage: true, clearUser: true});
      return null;
    }

    return this.persistToken(refreshedToken);
  }

  /** Returns null only when the refresh token was rejected; throws when the call failed. */
  private async doRefreshToken(token: Token): Promise<TokenResponse | null> {
    try {
      const {status, data} = await this.client.put('token', {
        refresh_token: token.refreshToken,
      });
      if (status !== 200) {
        return null;
      }

      return data;
    } catch (error) {
      if (error instanceof AxiosError && error.code === 'ERR_NETWORK') {
        throw new NetworkError();
      }
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return null;
      }
      // 5xx, timeouts and anything else are transient — never drop the session for them.
      throw error;
    }
  }

  private async resolvePicture(picture: Picture): Promise<string | null> {
    if (picture && typeof picture !== 'string') {
      return await this.uploadFile(picture, null, false);
    }
    return picture;
  }

  /**
   * Runs an authenticated API call, translating a 401 response into an
   * UnauthorizedError (so the caller can refresh the token) and swallowing
   * every other failure into the provided fallback value.
   */
  private async guarded<T>(fallback: T, call: () => Promise<T>): Promise<T> {
    try {
      return await call();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        throw new UnauthorizedError();
      }
      return fallback;
    }
  }

  private persistToken(response: TokenResponse): Token {
    const token = new Token(
      response.access_token,
      response.refresh_token,
      response.expiry_at,
      response.refresh_token_expiry_at,
    );
    localStorage.setItem(this.tokenStorage, token.serialize());
    runInAction(() => {
      this.token = token;
      this.loggedIn = true;
    });
    return token;
  }

  private resetAuthState(opts: { removeStorage?: boolean, clearUser?: boolean, loading?: boolean } = {}) {
    if (opts.removeStorage) {
      localStorage.removeItem(this.tokenStorage);
    }
    runInAction(() => {
      this.token = null;
      this.loggedIn = false;
      if (opts.clearUser) {
        this.loggedInUser = null;
      }
      if (opts.loading !== undefined) {
        this.isLoading = opts.loading;
      }
    });
  }
}
